import { Link } from 'react-router-dom';
import { useAboutSettings } from '@/hooks/useAboutSettings';
import { usePosts } from '@/hooks/usePosts';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import NewsletterSection from '@/pages/home/components/NewsletterSection';
import { useCategories } from '@/hooks/useCategories';
import Seo from '@/components/base/Seo';

export default function AboutPage() {
  const { settings } = useAboutSettings();
  const { getPublishedPosts } = usePosts();
  const { getActiveCategories } = useCategories();
  const activeCategories = getActiveCategories();
  const allPosts = getPublishedPosts();
  const { hero, story, mission, values, stats, cta } = settings;

  return (
    <div className="min-h-screen bg-background-50 font-body">
      <Seo title="Sobre o Miau de Casa" description="Conheça o Miau de Casa, nossa missão e o compromisso de produzir conteúdo útil para tutores e amantes de gatos." path="/sobre" />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[280px] sm:h-[340px] md:h-[460px] overflow-hidden">
        <img
          src={hero.image}
          alt={hero.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/70 via-foreground-950/40 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8 md:px-16 lg:px-24 max-w-7xl mx-auto w-full">
          <nav className="flex items-center gap-2 mb-3 md:mb-4 text-background-50/70 text-xs">
            <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">Início</Link>
            <i className="ri-arrow-right-s-line text-sm"></i>
            <span className="text-background-50">Sobre nós</span>
          </nav>
          <h1 className="font-heading font-black text-background-50 text-2xl sm:text-3xl md:text-5xl lg:text-6xl leading-tight mb-3 md:mb-4 max-w-2xl animate-fade-in-up">
            {hero.title}
          </h1>
          <p className="text-background-50/85 text-sm sm:text-base md:text-xl max-w-lg leading-relaxed animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section className="w-full bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {stats.items.map((stat, i) => (
              <div key={i} className={`py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-8 text-center ${i < 2 ? 'border-b md:border-b-0' : ''} ${i % 2 === 0 ? 'border-r border-primary-400/50 md:border-r' : 'md:border-r'} ${i === 3 || i === stats.items.length - 1 ? 'md:border-r-0' : ''} ${i < 2 ? 'border-primary-400/50' : ''}`}>
                <p className="font-heading font-black text-background-50 text-xl sm:text-2xl md:text-3xl leading-none mb-1">
                  {stat.value}
                </p>
                <p className="text-primary-100 text-xs md:text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NOSSA HISTÓRIA ── */}
      <section className="w-full bg-background-50 py-12 md:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-8 md:gap-12 lg:gap-20">
            {/* Text */}
            <div className="flex-1 order-2 lg:order-1">
              <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-3">
                Quem somos
              </span>
              <h2 className="font-heading font-bold text-foreground-950 text-2xl sm:text-3xl md:text-4xl leading-tight mb-6 md:mb-8">
                {story.sectionTitle}
              </h2>
              <div className="space-y-4">
                {story.paragraphs.map((p, i) => (
                  <p key={i} className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
              <Link
                to="/blog"
                className="group inline-flex items-center gap-2 mt-6 md:mt-8 bg-primary-500 hover:bg-primary-600 text-white text-sm font-bold px-5 py-2.5 md:px-6 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                Explorar nossos artigos
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
              </Link>
            </div>

            {/* Image */}
            <div className="w-full lg:w-[420px] h-[240px] sm:h-[320px] md:h-[440px] flex-shrink-0 order-1 lg:order-2 rounded-2xl overflow-hidden">
              <img
                src={story.image}
                alt={story.sectionTitle}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSÃO ── */}
      <section className="w-full bg-background-100 py-12 md:py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-3 md:mb-4">
            {mission.sectionTitle}
          </span>
          <blockquote className="font-heading font-black text-foreground-950 text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight mb-4 md:mb-6">
            &ldquo;{mission.mainText}&rdquo;
          </blockquote>
          <div className="w-12 md:w-16 h-0.5 md:h-1 bg-primary-500 rounded-full mx-auto mb-4 md:mb-6"></div>
          <p className="text-foreground-600 text-sm md:text-base lg:text-lg leading-relaxed max-w-2xl mx-auto">
            {mission.description}
          </p>
        </div>
      </section>

      {/* ── VALORES ── */}
      <section className="w-full bg-background-50 py-12 md:py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-12">
            <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-3">
              O que nos guia
            </span>
            <h2 className="font-heading font-bold text-foreground-950 text-2xl sm:text-3xl md:text-4xl">
              {values.sectionTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {values.items.map((item, i) => (
              <div
                key={i}
                className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 hover:border-primary-300 hover:-translate-y-1 transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms`, animationFillMode: 'both' }}
              >
                <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-primary-100 mb-3 md:mb-4">
                  <i className={`${item.icon} text-xl md:text-2xl text-primary-500`}></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-950 text-sm md:text-base mb-2">
                  {item.title}
                </h3>
                <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORIAS ── */}
      <section className="w-full bg-background-100 py-12 md:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <span className="text-primary-500 text-xs font-bold tracking-widest uppercase block mb-3">
              Explore nosso conteúdo
            </span>
            <h2 className="font-heading font-bold text-foreground-950 text-2xl sm:text-3xl md:text-4xl">
              O que você encontra aqui
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {activeCategories.map((cat, i) => (
              <Link
                key={cat.id}
                to={`/categoria/${cat.id}`}
                className="group cursor-pointer animate-fade-in-up"
                style={{ animationDelay: `${i * 60}ms`, animationFillMode: 'both' }}
              >
                <div className="bg-background-50 border border-background-200 rounded-xl p-3 sm:p-4 md:p-6 flex flex-col items-center text-center hover:-translate-y-1.5 hover:border-primary-300 transition-all duration-300">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-primary-100/60 group-hover:bg-primary-100 transition-colors mb-2 md:mb-3">
                    <i className={`${cat.icon} text-lg sm:text-xl md:text-2xl text-primary-500`}></i>
                  </div>
                  <span className="font-bold text-foreground-950 text-xs sm:text-sm mb-1 group-hover:text-primary-700 transition-colors">
                    {cat.label}
                  </span>
                  <span className="text-primary-500 text-xs font-semibold inline-flex items-center gap-0.5 group-hover:gap-1.5 transition-all">
                    Ver artigos <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="w-full bg-primary-500 py-12 md:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <i className="ri-heart-fill text-background-50/30 text-3xl md:text-5xl mb-4 md:mb-6 block"></i>
          <h2 className="font-heading font-black text-background-50 text-2xl sm:text-3xl md:text-4xl leading-tight mb-3 md:mb-4">
            {cta.title}
          </h2>
          <p className="text-primary-100 text-sm md:text-base lg:text-lg leading-relaxed mb-6 md:mb-8">
            {cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/blog"
              className="bg-background-50 text-primary-600 hover:bg-background-100 font-bold text-sm px-5 py-2.5 md:px-6 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
            >
              Ler os artigos
            </Link>
            <Link
              to="/"
              className="border-2 border-background-50/50 text-background-50 hover:border-background-50 font-bold text-sm px-5 py-2.5 md:px-6 md:py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
            >
              Ir para a home
            </Link>
          </div>
        </div>
      </section>

      <NewsletterSection />
      <Footer popularPosts={allPosts} />
    </div>
  );
}
